/**
 * A single Deliveries-table row (one GoodsReceiptNote).
 *
 * Renders its cells dynamically from the visible column configuration,
 * mirroring the Schedule Lines ScheduleLineRow pattern. All cell markup,
 * badges and the multi-branch Action column are preserved verbatim from the
 * previous inline implementation — only the container was made config-driven.
 *
 * Wrapped with React.memo to prevent unnecessary re-renders when parent state
 * changes (e.g., text input keystrokes) but this row's data hasn't changed.
 */
import { memo } from 'react';
import type { ReactNode } from 'react';
import { Eye, Link2, CheckCircle, HelpCircle, XCircle, Check, Gavel } from 'lucide-react';

import PoLineLink from '../../../common/PoLineLink';
import PoHeaderLink from '../../../common/PoHeaderLink';
import type { GoodsReceiptNote } from '../../../../lib/api/types/grn-types';
import type { DeliveryColumnConfig } from './deliveryColumnConfig';
import { countsAsOnTime } from '../../../../lib/onTimeStatus';  // CR-6518
import { isQuantityOnlyAppeal } from './appealEligibility';

interface DeliveryRowProps {
  grn: GoodsReceiptNote;
  visibleColumns: DeliveryColumnConfig[];
  isSupplier: boolean;
  scheduleCollabActive: boolean;
  hideAppealForSupplier: boolean;
  /** Whether the current user may re-assign a GRN to a schedule line. */
  canReassign: boolean;
  formatDate: (dateString: string | undefined) => string;
  formatQuantity: (qty: string | number) => string;
  getOntimeStatusBadge: (
    status: string,
    overridden?: boolean,
    displayLabel?: string,
    allocations?: readonly { on_time_status?: string | null }[] | null,
  ) => ReactNode;
  getAppealStatusBadge: (status: string, isCustomerAppeal?: boolean, workflowStatus?: string, onTimeStatus?: string, qtyMismatch?: boolean) => ReactNode;
  onViewDetails: (grn: GoodsReceiptNote) => void;
  onReassign: (grn: GoodsReceiptNote) => void;
  onOpenAppealResponse: (grn: GoodsReceiptNote) => void;
  onAcknowledge: (grn: GoodsReceiptNote) => void;
  onAppeal: (grn: GoodsReceiptNote) => void;
  onCancelAppeal: (grn: GoodsReceiptNote, e: React.MouseEvent) => void;
  /** Bulk multi-select is active: render a leading cell on EVERY row so
   *  columns stay aligned with the header. The checkbox inside only shows when
   *  the row is ``selectable`` (a bulk acknowledge or appeal applies, CR-8214). */
  bulkMode?: boolean;
  /** Whether a bulk action applies to this row — controls the checkbox, not the cell. */
  selectable?: boolean;
  /** Whether this row is currently selected for a bulk action. */
  selected?: boolean;
  /** Toggle this row's bulk selection. */
  onToggleSelect?: (grn: GoodsReceiptNote) => void;
}

/**
 * Renders one Deliveries table row with only the visible columns.
 */
function DeliveryRowInner({
  grn,
  visibleColumns,
  isSupplier,
  scheduleCollabActive,
  hideAppealForSupplier,
  canReassign,
  formatDate,
  formatQuantity,
  getOntimeStatusBadge,
  getAppealStatusBadge,
  onViewDetails,
  onReassign,
  onOpenAppealResponse,
  onAcknowledge,
  onAppeal,
  onCancelAppeal,
  bulkMode,
  selectable,
  selected,
  onToggleSelect,
}: DeliveryRowProps) {
  // Early / Late: Acknowledge + Appeal. On time with the wrong quantity: Appeal
  // only (on quantity), and only while an appeal can actually be raised.
  const exception = !countsAsOnTime(grn.on_time_status);
  const quantityOnly = isQuantityOnlyAppeal(grn) && grn.can_appeal === true;
  const renderCell = (colId: string): ReactNode => {
    switch (colId) {
      case 'grn_number':
        return <td key={colId} className="py-3 px-4 font-medium">{grn.grn_number}</td>;
      case 'view':
        return (
          <td key={colId} className="py-3 px-4">
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); onViewDetails(grn); }}
                className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                title="View Details"
              >
                <Eye className="h-4.5 w-4.5 text-blue-600" />
              </button>
              {/* Manually re-assign this GRN to a schedule line — superusers and
                  Django staff (Scheduled installs), mirroring the backend gate. */}
              {canReassign && (
                <button
                  onClick={(e) => { e.stopPropagation(); onReassign(grn); }}
                  className="p-1.5 hover:bg-amber-50 rounded transition-colors"
                  title="Re-assign to schedule line"
                >
                  <Link2 className="h-4.5 w-4.5 text-amber-600" />
                </button>
              )}
            </div>
          </td>
        );
      case 'action':
        return (
          <td key={colId} className="py-3 px-4">
            {/* CR-6517: an overridden GRN is resolved — no acknowledge/appeal/
                review controls at all, regardless of appeal state. (An OTIF-review
                acknowledgement keeps the fail, so the supplier may still appeal.) */}
            {grn.otif_overridden ? (
              <span className="text-xs text-muted-foreground">-</span>
            ) : /* CR-4786: Customer — supplier appeal awaiting response. */
            !isSupplier
                && grn.latest_appeal
                && grn.latest_appeal.status === 'awaiting_response'
                && !grn.latest_appeal.is_customer_appeal ? (
              <div className="flex gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenAppealResponse(grn); }}
                  className="p-1.5 hover:bg-green-50 rounded transition-colors"
                  title="Accept appeal"
                >
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenAppealResponse(grn); }}
                  className="p-1.5 hover:bg-yellow-50 rounded transition-colors"
                  title="Query supplier"
                >
                  <HelpCircle className="h-5 w-5 text-yellow-600" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenAppealResponse(grn); }}
                  className="p-1.5 hover:bg-red-50 rounded transition-colors"
                  title="Reject appeal"
                >
                  <XCircle className="h-5 w-5 text-red-600" />
                </button>
              </div>
            ) : /* CR-4651: Customer — Acknowledge for Early/Late pending.
                   CR-6313: plus Appeal, matching the supplier branch below.
                   The customer had Acknowledge only while the supplier got
                   Acknowledge + Appeal, so the two sides of the same table
                   disagreed about whether this row could be appealed at all.
                   `hideAppealForSupplier` is deliberately not applied — that
                   toggle hides the control for SUPPLIER users. */
            !isSupplier
                && grn.workflow_status === 'pending_review'
                && (exception || quantityOnly)  // CR-6518: an accepted override is not an exception
                && !(grn.latest_appeal && ['rejected', 'accepted'].includes(grn.latest_appeal.status)) ? (
              <div className="flex gap-1">
                {exception && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAcknowledge(grn); }}
                    className="p-1.5 hover:bg-green-50 rounded transition-colors"
                    title="Acknowledge"
                  >
                    <Check className="h-5 w-5 text-green-600" />
                  </button>
                )}
                {/* CR-7319: a gavel, not the warning triangle — the triangle
                    read as a status marker, so the shortcut went unnoticed. */}
                {grn.can_appeal && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAppeal(grn); }}
                    className="p-1.5 hover:bg-orange-50 rounded transition-colors"
                    title="Raise appeal"
                    aria-label={`Raise appeal on ${grn.grn_number}`}
                  >
                    <Gavel className="h-5 w-5 text-orange-500" />
                  </button>
                )}
              </div>
            ) : /* CR-4869: Supplier — cancel own appeal awaiting customer response. */
            isSupplier
                && !hideAppealForSupplier
                && grn.latest_appeal
                && grn.latest_appeal.status === 'awaiting_response'
                && !grn.latest_appeal.is_customer_appeal ? (
              <button
                onClick={(e) => onCancelAppeal(grn, e)}
                className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                title="Cancel Appeal"
              >
                <XCircle className="h-5 w-5 text-gray-500" />
              </button>
            ) : /* CR-4601 + CR-4651: Supplier — Acknowledge + Appeal icons. */
            isSupplier
                && grn.workflow_status === 'pending_review'
                && (exception || (quantityOnly && !hideAppealForSupplier))  // CR-6518: an accepted override is not an exception
                && !(grn.latest_appeal && ['rejected', 'accepted'].includes(grn.latest_appeal.status)) ? (
              <div className="flex gap-1">
                {exception && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAcknowledge(grn); }}
                    className="p-1.5 hover:bg-green-50 rounded transition-colors"
                    title="Acknowledge"
                  >
                    <Check className="h-5 w-5 text-green-600" />
                  </button>
                )}
                {/* CR-7319: same control as the customer branch above. */}
                {grn.can_appeal && !hideAppealForSupplier && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onAppeal(grn); }}
                    className="p-1.5 hover:bg-orange-50 rounded transition-colors"
                    title="Raise appeal"
                    aria-label={`Raise appeal on ${grn.grn_number}`}
                  >
                    <Gavel className="h-5 w-5 text-orange-500" />
                  </button>
                )}
              </div>
            ) : /* CR-4601 rework: Supplier — acknowledged state. */
            isSupplier && grn.workflow_status === 'acknowledged' ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3" />
                Acknowledged
              </span>
            ) : /* Supplier — update queried appeal. */
            isSupplier && !hideAppealForSupplier && grn.latest_appeal?.status === 'queried' ? (
              <button
                onClick={(e) => { e.stopPropagation(); onViewDetails(grn); }}
                className="p-1.5 hover:bg-yellow-50 rounded transition-colors"
                title="Update Appeal"
                aria-label={`Update appeal on ${grn.grn_number}`}
              >
                {/* CR-7319: the appeal glyph, in the queried-state yellow. */}
                <Gavel className="h-5 w-5 text-yellow-500" />
              </button>
            ) : (
              <span className="text-xs text-muted-foreground">-</span>
            )}
          </td>
        );
      case 'on_time_status':
        return <td key={colId} className="py-3 px-4">{getOntimeStatusBadge(grn.on_time_status || '', grn.otif_overridden || grn.otif_acknowledged, grn.on_time_status_display, grn.allocations)}</td>;
      case 'appeal_status':
        return <td key={colId} className="py-3 px-4">{getAppealStatusBadge(grn.latest_appeal?.status || 'none', grn.latest_appeal?.is_customer_appeal, grn.workflow_status, grn.on_time_status, quantityOnly)}</td>;
      case 'delivery_date':
        return <td key={colId} className="py-3 px-4">{formatDate(grn.receipt_date)}</td>;
      case 'customer':
        return <td key={colId} className="py-3 px-4">{grn.customer_details?.name || grn.customer_name || '-'}</td>;
      case 'supplier_group':
        return <td key={colId} className="py-3 px-4">{grn.supplier_group || '-'}</td>;
      case 'supplier':
        return <td key={colId} className="py-3 px-4"><span className="text-primary">{grn.supplier_name}</span></td>;
      case 'po_number':
        // CR-1154: PO number links to the PO header (was ?po=<number>, which
        // resolved to nothing).
        return (
          <td key={colId} className="py-3 px-4 text-right font-medium">
            <PoHeaderLink poLineId={grn.po_line} poNumber={grn.po_number}>{grn.po_number}</PoHeaderLink>
          </td>
        );
      case 'line_number':
        // CR-1154: deep-link to the PO line item detail.
        return (
          <td key={colId} className="py-3 px-4 text-right">
            <PoLineLink poLineId={grn.po_line}>{grn.line_number ?? '-'}</PoLineLink>
          </td>
        );
      case 'sched_line':
        return <td key={colId} className="py-3 px-4 text-right">{grn.schedule_line_number ?? '-'}</td>;
      case 'po_line_status':
        return <td key={colId} className="py-3 px-4">{grn.po_line_status || '-'}</td>;
      case 'part_number':
        return <td key={colId} className="py-3 px-4 font-mono">{grn.part_number}</td>;
      case 'part_description':
        return <td key={colId} className="py-3 px-4">{grn.part_description || '-'}</td>;
      case 'qty_ordered':
        return <td key={colId} className="py-3 px-4 text-right">{formatQuantity(Number(scheduleCollabActive ? (grn.schedule_qty ?? grn.quantity_ordered ?? 0) : (grn.quantity_ordered ?? 0)))}</td>;
      case 'qty_received':
        return <td key={colId} className="py-3 px-4 text-right">{formatQuantity(Number(grn.quantity_received))}</td>;
      case 'unit_price':
        return <td key={colId} className="py-3 px-4 text-right">{parseFloat(grn.unit_price || '0').toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>;
      case 'ppv':
        return (
          <td key={colId} className="py-3 px-4">{(() => {
            const ppvNum = grn.ppv ? parseFloat(grn.ppv) : null;
            if (ppvNum === null || isNaN(ppvNum)) return '-';
            const badge = ppvNum > 0
              ? 'bg-red-100 text-red-800'
              : ppvNum < 0
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-gray-100 text-gray-800';
            return (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge}`}>
                {ppvNum.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            );
          })()}</td>
        );
      case 'currency':
        return <td key={colId} className="py-3 px-4 text-right">{grn.currency || '-'}</td>;
      case 'total_value':
        return <td key={colId} className="py-3 px-4 text-right">{Number(grn.total_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>;
      case 'unit_price_converted':
        return <td key={colId} className="py-3 px-4 text-right">{parseFloat(grn.unit_price_converted || '0').toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>;
      case 'total_value_converted':
        return <td key={colId} className="py-3 px-4 text-right">{parseFloat(grn.total_value_converted || '0').toLocaleString('en-GB', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>;
      case 'release_note_number':
        return <td key={colId} className="py-3 px-4 text-right">{grn.release_note_number || '-'}</td>;
      case 'mrp_date':
        return <td key={colId} className="py-3 px-4">{formatDate(grn.mrp_date)}</td>;
      case 'review_date':
        return <td key={colId} className="py-3 px-4">{formatDate(grn.review_date)}</td>;
      case 'odd':
        // Customer review 11 Sep (item 36): ODD = the date the On-Time
        // verdict was measured against (backend verdict engine: schedule slot
        // -> line ODD -> delivery date), so this column can never contradict
        // the On Time Status beside it. Older payloads fall back to the
        // previous chain.
        return <td key={colId} className="py-3 px-4">{(() => {
          const odd = grn.verdict_anchor_date ?? grn.schedule_date ?? grn.original_delivery_date ?? grn.po_delivery_date;
          return odd ? formatDate(odd) : '-';
        })()}</td>;
      default:
        return null;
    }
  };

  return (
    <tr
      onClick={() => onViewDetails(grn)}
      className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
    >
      {bulkMode && (
        <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
          {selectable && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect?.(grn)}
              className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
              title="Select for bulk action"
              aria-label={`Select ${grn.grn_number}`}
            />
          )}
        </td>
      )}
      {visibleColumns.map((col) => renderCell(col.id))}
    </tr>
  );
}

/**
 * Memoized DeliveryRow — only re-renders when its props actually change.
 * Prevents the entire table from re-rendering on every text input keystroke.
 */
const DeliveryRow = memo(DeliveryRowInner);
export default DeliveryRow;
